/**
 * BudgetSuiviCard — G3 : le suivi Budget en LECTURE SEULE (vague 4d, §5-5).
 *
 * Décision actée : chaque brique consommatrice VOIT (restant par secteur,
 * derniers actes budgétés) mais ne MODIFIE pas — les enveloppes appartiennent
 * à la Comptabilité. Repliée par défaut : elle informe sans encombrer.
 */

import { useEffect, useState } from 'react';
import { api, type BudgetSuivi } from '@/lib/api';
import { Card, Badge } from '@/components/ui';
import { Wallet, ChevronDown, ChevronUp } from 'lucide-react';

function euros(n: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}

export function BudgetSuiviCard() {
  const [data, setData] = useState<BudgetSuivi | null>(null);
  const [ouvert, setOuvert] = useState(false);

  useEffect(() => {
    api.budgets.suivi().then(setData).catch(() => setData(null));
  }, []);

  if (!data || data.secteurs.length === 0) return null;   // rien à montrer, rien d'affiché

  return (
    <Card variant="glass" padded>
      <button type="button" onClick={() => setOuvert(!ouvert)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={ouvert}>
        <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
          <Wallet className="h-4 w-4" aria-hidden="true" /> Budget {data.annee}
          <span className="text-xs font-normal text-ink-muted">
            — {euros(data.global.restant)} restants
          </span>
          {data.secteurs.some((s) => s.enDepassement) && (
            <Badge variant="urgent" size="sm">Dépassement</Badge>
          )}
        </span>
        {ouvert
          ? <ChevronUp className="h-4 w-4 text-ink-muted" aria-hidden="true" />
          : <ChevronDown className="h-4 w-4 text-ink-muted" aria-hidden="true" />}
      </button>

      {ouvert && (
        <div className="mt-2 space-y-1 border-t border-border/60 pt-2">
          {data.secteurs.map((s) => (
            <p key={s.id} className="flex items-center justify-between text-xs">
              <span className="text-ink">{s.secteur}</span>
              <span className={s.enDepassement ? 'text-danger' : 'text-ink-muted'}>
                {euros(s.restant)} restants sur {euros(s.montantAlloue)}
              </span>
            </p>
          ))}
          {(data.mouvements ?? []).slice(0, 5).map((m) => (
            <p key={m.id} className="text-2xs text-ink-muted">
              {m.sens === 'debit' ? '−' : '+'}{euros(m.montant)} · {m.secteur} · {m.motif}
            </p>
          ))}
          <p className="text-2xs text-ink-muted">
            Lecture seule — les enveloppes se règlent dans la Comptabilité.
          </p>
        </div>
      )}
    </Card>
  );
}
